<template>
    <div class="max-h-[70vh] overflow-y-auto rounded-md border border-gray-200">
        <!-- Sticky column header -->
        <div
            class="sticky top-0 z-10 grid grid-cols-[minmax(0,1fr)_6rem_6rem_6rem_6rem] border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide shadow-sm"
        >
            <button
                type="button"
                class="flex items-center gap-1 px-4 py-2 text-left text-gray-600 hover:text-gray-900"
                @click="cycleSort"
            >
                Category
                <ChevronUpIcon v-if="sortDir === 'asc'" class="h-3.5 w-3.5" />
                <ChevronDownIcon
                    v-else-if="sortDir === 'desc'"
                    class="h-3.5 w-3.5"
                />
                <ChevronUpDownIcon v-else class="h-3.5 w-3.5 text-gray-400" />
            </button>
            <div
                v-for="col in countColumns"
                :key="col.key"
                class="flex items-center justify-end px-3 py-2"
                :class="col.headerClass"
            >
                {{ col.label }}
            </div>
        </div>

        <!-- Rows (flattened, only descendants of expanded nodes are shown) -->
        <div
            v-for="row in visibleRows"
            :key="row.id"
            class="group grid grid-cols-[minmax(0,1fr)_6rem_6rem_6rem_6rem] items-stretch border-b border-gray-100 transition-colors last:border-b-0"
        >
            <div
                class="flex min-w-0 items-center gap-1.5 py-1.5 pr-3 transition-colors group-hover:bg-gray-200"
                :style="{ paddingLeft: 1 + row.depth * 1.25 + 'rem' }"
            >
                <button
                    v-if="row.hasChildren"
                    type="button"
                    class="flex-shrink-0 text-gray-400 hover:text-gray-700"
                    :aria-label="expanded[row.id] ? 'Collapse' : 'Expand'"
                    @click="toggle(row.id)"
                >
                    <ChevronRightIcon
                        class="h-4 w-4 transition-transform"
                        :class="{ 'rotate-90': expanded[row.id] }"
                    />
                </button>
                <span v-else class="inline-block w-4 flex-shrink-0"></span>
                <button
                    type="button"
                    class="flex-shrink-0 cursor-pointer text-gray-300 hover:text-gray-500"
                    :title="`Copy GUID: ${row.uuid}`"
                    :aria-label="`Copy GUID for ${row.category}`"
                    @click="copyUuid(row.uuid)"
                >
                    <SvgIcon icon="clipboard" size="sm" />
                </button>
                <RouterLink
                    :to="`/prep/banks/${bankId}/categories/${row.id}`"
                    class="truncate text-blue-600 hover:text-blue-800 hover:underline"
                    :class="{ 'font-semibold': row.depth === 0 }"
                    >{{ row.category }}</RouterLink
                >
            </div>
            <div
                v-for="col in countColumns"
                :key="col.key"
                class="flex items-center justify-end px-3 py-1.5 text-sm font-semibold tabular-nums transition-colors"
                :class="col.cellClass"
            >
                {{ row[col.key] }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * Renders a question bank's category hierarchy (Category -> subcategory ->
 * topic) as an aligned grid: the tree lives in the first column and the
 * total/open/reserved/draft counts sit in fixed, right-aligned, colour-coded
 * columns under a sticky header so they can be scanned vertically (e.g.
 * against a spreadsheet).
 *
 * The tree is flattened to a flat list of visible rows so columns line up at
 * every depth; nodes with children are collapsible via the shared `expanded`
 * map owned by the page.
 */
import type { BankCategoryCount } from "@/types";
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import {
    ChevronRightIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronUpDownIcon,
} from "@heroicons/vue/20/solid";
import { copyToClipboard } from "@/utils";
import $events from "@/event-bus";

interface Props {
    categories: BankCategoryCount[];
    bankId: number;
    /** Shared expand state keyed by category id, owned by the page so the
     * whole tree can be expanded/collapsed at once. */
    expanded: Record<number, boolean>;
}

const props = defineProps<Props>();

type CountKey = "open" | "reserved" | "draft" | "total";

const countColumns: {
    key: CountKey;
    label: string;
    headerClass: string;
    cellClass: string;
}[] = [
    {
        key: "open",
        label: "Open",
        headerClass: "bg-green-100/70 text-green-800",
        cellClass: "bg-green-50/70 text-green-800 group-hover:bg-green-100",
    },
    {
        key: "reserved",
        label: "Reserved",
        headerClass: "bg-blue-100/70 text-blue-800",
        cellClass: "bg-blue-50/70 text-blue-800 group-hover:bg-blue-100",
    },
    {
        key: "draft",
        label: "Draft",
        headerClass: "bg-yellow-100/70 text-yellow-800",
        cellClass: "bg-yellow-50/70 text-yellow-800 group-hover:bg-yellow-100",
    },
    {
        key: "total",
        label: "Total",
        headerClass: "bg-gray-200/70 text-gray-700",
        cellClass: "bg-gray-100/70 text-gray-900 group-hover:bg-gray-200",
    },
];

interface Row {
    id: number;
    uuid: string;
    category: string;
    depth: number;
    hasChildren: boolean;
    open: number;
    reserved: number;
    draft: number;
    total: number;
}

// null = the bank's default order; otherwise sort siblings alphabetically at
// every level while preserving the hierarchy.
const sortDir = ref<"asc" | "desc" | null>(null);

const cycleSort = (): void => {
    sortDir.value =
        sortDir.value === null
            ? "asc"
            : sortDir.value === "asc"
            ? "desc"
            : null;
};

const visibleRows = computed<Row[]>(() => {
    const rows: Row[] = [];

    const order = (nodes: BankCategoryCount[]): BankCategoryCount[] => {
        if (!sortDir.value) return nodes;
        const factor = sortDir.value === "asc" ? 1 : -1;
        return [...nodes].sort(
            (a, b) =>
                factor *
                a.category.localeCompare(b.category, undefined, {
                    sensitivity: "base",
                })
        );
    };

    const walk = (nodes: BankCategoryCount[], depth: number): void => {
        for (const node of order(nodes)) {
            const hasChildren = !!node.children?.length;
            rows.push({
                id: node.id,
                uuid: node.uuid,
                category: node.category,
                depth,
                hasChildren,
                open: node.open,
                reserved: node.reserved,
                draft: node.draft,
                total: node.total,
            });
            if (hasChildren && props.expanded[node.id]) {
                walk(node.children, depth + 1);
            }
        }
    };

    walk(props.categories, 0);
    return rows;
});

const toggle = (id: number): void => {
    props.expanded[id] = !props.expanded[id];
};

const copyUuid = async (uuid: string): Promise<void> => {
    await copyToClipboard(uuid);
    $events.$emit("toastNotification", {
        status: "success",
        heading: "Copied GUID to clipboard!",
    });
};
</script>
